<template>
    <div v-if="isVisible" class="options-menu-overlay" @click="closeMenu">
        <div class="options-menu" ref="menuRef" @click.stop>
            <div class="options-content">
                <div class="options-section">
                    <div class="options-buttons">
                        <button
                            v-if="inApp"
                            class="action-button home-button"
                            @click="returnHome"
                            ref="homeButton">
                            <IconHome class="icon"/>
                        </button>

                        <button
                            class="action-button reboot-button"
                            @click="rebootSystem"
                            ref="rebootButton">
                            <IconPower class="icon"/>
                        </button>
                    </div>
                </div>

                <div class="site-name">{{ systemInfo.handshakeInfo?.siteName }}</div>

                <dl class="info-list">
                    <template v-for="item in activeStatusItems" :key="item.label">
                        <dt>{{ item.label }}</dt>
                        <dd>{{ item.value || "..."}}</dd>
                    </template>
                </dl>

                <h3 class="info-heading">Customer Support</h3>

                <dl class="info-list">
                    <template v-for="item in supportItems" :key="item.label">
                        <dt>{{ item.label }}</dt>
                        <dd>{{ item.value || "..."}}</dd>
                    </template>
                </dl>

<!--			<div v-if="startupMode === 'virtual-paired'" class="reset-section">-->
<!--				<div class="reset-section">-->
<!--					<button-->
<!--							class="action-button reset-button"-->
<!--							@click="showResetModal = true"-->
<!--							ref="resetButton">-->
<!--						Reset Display-->
<!--					</button>-->
<!--				</div>-->
            </div>
        </div>
    </div>
	<ResetDisplay
			:show="showResetModal"
			@confirm="resetDisplay"
			@cancel="showResetModal = false"
	/>
</template>

<script setup>
import {ref, computed, onMounted, onUnmounted, watch, nextTick, reactive} from 'vue';
import gsap from 'gsap';
import {params, systemInfo} from "@/systemInfo.js";
import {initOptionsMenuKeyboard} from "@/services/keyboard.js";
import IconPower from "@/components/icons/IconPower.vue";
import IconHome from "@/components/icons/IconHome.vue";
import ResetDisplay from "@/components/modals/ResetDisplay.vue";
import {startupMode} from "@/services/startup.js";
import {getDeviceId, getOrCreateDeviceId} from "@/services/virtualDisplay.js";

const statusItems = reactive([
    {label: 'Version', value: systemInfo.version, error: false},
    {label: 'Serial Number', value: '...', error: false},
    {label: 'Site ID', value: '...', error: false},
    {label: 'Prizes', value: '...', error: false},
    {label: 'Bar Hero', value: '...', error: false}
]);

const systemInfoItems = computed(() => [
    {label: 'Site ID', value: systemInfo.siteId},
    {label: 'Serial Number', value: systemInfo.serialNumber},
    {label: 'Version', value: `${systemInfo.version} | ${systemInfo.buildTimestamp}`},
    {label: 'Remote', value: systemInfo.remote ? 'Connected' : 'Disconnected'},
	{label: 'Bar Hero', value: systemInfo.prizeEnabled ? 'Enabled' : 'Disabled'},
	{label: 'Screen (D)', value: systemInfo.screen},
	// {label: 'IP Address', value: systemInfo.ip},
    {label: 'Local Time', value: systemInfo.time},
    {label: 'Last Reboot', value: systemInfo.lastReboot},
    {label: 'Auto Reboot', value: systemInfo.nextRebootTime}
]);

const activeStatusItems = computed(() => {
	if (startupMode.value === 'virtual-paired') {
		// Remove Serial Number, add Device ID for virtual displays
		const items = systemInfoItems.value.filter(item => item.label !== 'Serial Number');
		items.splice(1, 0, {
			label: 'Display ID',
			key: 'deviceId',
			value: getDeviceId().toUpperCase().split('-')[0], // Show short version of UUID
			error: false
		});
		return items;
	}
	// Default Hardware view
	return systemInfoItems.value;
});

const supportInfo = reactive({
    phone: import.meta.env.VITE_CS_PHONE || '...',
    email: import.meta.env.VITE_CS_EMAIL || '...',
    website: import.meta.env.VITE_CS_WEB || '...'
});

const supportItems = computed(() => [
    {label: 'Phone', value: supportInfo.phone},
    {label: 'Email', value: supportInfo.email},
    {label: 'Website', value: supportInfo.website}
]);

const props = defineProps({
    show: {
        type: Boolean,
        default: false
    },
    inApp: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits(['close', 'reboot', 'return-home']);

// Menu timer
const menuTimer = ref(null);
const menuTimeLeft = ref(params.modalTimeout);
const menuRef = ref(null);
const rebootButton = ref(null);
const homeButton = ref(null);
const isVisible = ref(false);
const isAnimating = ref(false);
const showResetModal = ref(false);
const resetButton = ref(null);

// Reboot system
const rebootSystem = () => {
    if (isAnimating.value) return;
    emit('reboot');
    closeMenu();
};

const returnHome = () => {
    if (isAnimating.value) return;
    emit('return-home');
    closeMenu();
};

const resetDisplay = async () => {
	showResetModal.value = false;
	
	localStorage.clear();
	sessionStorage.clear();
	
	if ('caches' in window) {
		const cacheNames = await caches.keys();
		await Promise.all(cacheNames.map(name => caches.delete(name)));
	}
	
	if (window.indexedDB && indexedDB.databases) {
		const dbs = await indexedDB.databases();
		dbs.forEach(db => {
			if (db.name) indexedDB.deleteDatabase(db.name);
		});
	}
	
	// TODO: Need to send API call to remove this display from the site.
	window.location.reload();
};

// Start menu timer
const startMenuTimer = () => {
    menuTimeLeft.value = 300;
    clearMenuTimer();

    menuTimer.value = setInterval(() => {
        menuTimeLeft.value--;
        if (menuTimeLeft.value <= 0) {
            closeMenu();
        }
    }, 1000);
};

// Clear menu timer
const clearMenuTimer = () => {
    if (menuTimer.value) {
        clearInterval(menuTimer.value);
        menuTimer.value = null;
    }
};

// Ensure element is visible in scrollable container
const ensureButtonVisible = (button) => {
    if (!button || !menuRef.value) return;

    const menuRect = menuRef.value.getBoundingClientRect();
    const buttonRect = button.getBoundingClientRect();

    if (buttonRect.top < menuRect.top) {
        menuRef.value.scrollBy({
            top: buttonRect.top - menuRect.top - 20,
            behavior: 'smooth'
        });
    } else if (buttonRect.bottom > menuRect.bottom) {
        menuRef.value.scrollBy({
            top: buttonRect.bottom - menuRect.bottom + 20,
            behavior: 'smooth'
        });
    }
};

// Animate menu opening
const animateMenuOpen = () => {
    if (!menuRef.value) return;

    isAnimating.value = true;
    gsap.killTweensOf(menuRef.value);

    gsap.fromTo(
        menuRef.value,
        {
            opacity: 0,
            scale: 0,
            borderTopRightRadius: 'calc(var(--border-radius-xl) * 7)',
            transformOrigin: 'top right'
        },
        {
            opacity: 1,
            scale: 1,
            borderTopRightRadius: 'var(--border-radius-xl)',
            duration: 0.5,
            ease: 'power2.out',
            onComplete: () => {
                isAnimating.value = false;

                // Initialize keyboard navigation for options menu
                nextTick(() => {
                    const buttons = [];
                    if (rebootButton.value) buttons.push(rebootButton.value);
                    if (homeButton.value && props.inApp) buttons.push(homeButton.value);

                    initOptionsMenuKeyboard(buttons, ensureButtonVisible, closeMenu);
                });
            }
        }
    );
};

// Animate menu closing
const animateMenuClose = () => {
    if (!menuRef.value) return Promise.resolve();

    isAnimating.value = true;
    gsap.killTweensOf(menuRef.value);

    return new Promise(resolve => {

        gsap.to(
            menuRef.value,
            {
                opacity: 0,
                scale: 0,
                transformOrigin: 'top right',
                duration: 0.5,
                ease: 'power2.out',
                onComplete: () => {
                    isAnimating.value = false;
                    isVisible.value = false;
                    emit('close');
                    resolve();
                }
            }
        );
    });
};

// Watch for show prop changes
watch(() => props.show, (newValue) => {
    if (newValue) {
        startMenuTimer();

        if (isVisible.value) {
            animateMenuOpen();
            return;
        }

        isVisible.value = true;
        nextTick(() => animateMenuOpen());
    } else if (isVisible.value) {
        clearMenuTimer();
        animateMenuClose();
    }
});

// Close menu
const closeMenu = () => {
    clearMenuTimer();
    emit('close');
};

onMounted(() => {
    // No keyboard event listeners here - all handled by keyboard.js
});

// Clean up on unmount
onUnmounted(() => {
    clearMenuTimer();
});

// Expose methods to parent
defineExpose({
    closeMenu,
    animateMenuClose
});
</script>

<style scoped>
.options-menu-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: calc(var(--cqw) * 100);
    height: calc(var(--cqh) * 100);
    display: flex;
    justify-content: end;
    align-items: start;
    z-index: 1000;
    padding-top: var(--spacing-lg);
    padding-bottom: var(--spacing-xl);
}

.options-menu {
    width: calc(var(--cqw) * 35);
    max-height: 100%;
    border: var(--border-thin) var(--color-primary-light);
    border-radius: var(--border-radius-xl);
    border-top-right-radius: var(--border-radius-lg);
    box-shadow: var(--shadow-lg);
    overflow-y: auto;
    margin-right: var(--spacing-xl);
    font-size: var(--font-size-sm);
    background: var(--color-popup);
    backdrop-filter: var(--blur-popup);
	-webkit-backdrop-filter: var(--blur-popup);
}

.options-content {
    padding: var(--spacing-md);
}

.options-section {
    margin-bottom: var(--spacing-sm);
}

.options-buttons {
    display: flex;
    gap: var(--spacing-md);
    border-bottom: var(--border-thin) var(--color-text-secondary);
    flex-wrap: wrap;
}

.icon {
    height: calc(var(--cqw) * 3);
    width: auto;
    margin: var(--spacing-sm);
}

.action-button {
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: var(--border-radius-md);
    font-size: var(--font-size-reg);
    font-weight: var(--font-weight-semibold);
    cursor: pointer;
    margin-bottom: var(--spacing-sm);
    border: none;
    transition: all var(--transition-normal);
}

.reboot-button {
    color: var(--color-text);
    text-shadow: var(--shadow-sm);
}

.site-name {
    font-size: var(--font-size-md);
    color: var(--color-text);
    /* border-bottom: 0.1vw solid var(--color-text-secondary); */
    /* margin-bottom: var(--spacing-md); */
    padding-bottom: var(--spacing-sm);
}

.info-heading {
    font-size: var(--font-size-reg);
    font-weight: var(--font-weight-medium);
    color: var(--color-text-secondary);
    margin-top: var(--spacing-md);
}

.info-list {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr);
    column-gap: var(--spacing-md);
    align-items: baseline;
}

.info-list dt,
.info-list dd {
    padding: var(--spacing-xs) 0;
    border-bottom: var(--border-thin) var(--color-popup-element);
    font-size: var(--font-size-reg);
    line-height: 1.25;
}

.info-list dt {
    font-weight: var(--font-weight-semibold);
    color: var(--color-text-secondary);
}

.info-list dd {
    text-align: end;
    color: var(--color-text);
    overflow-wrap: anywhere;
    font-variant-numeric: tabular-nums;
}

.info-list > :nth-last-child(-n + 2) {
    border-bottom: none;
}

.reset-section {
	margin-top: var(--spacing-lg);
	display: flex;
	justify-content: center;
}

.reset-button {
	width: 100%;
	padding: var(--spacing-sm) var(--spacing-md);
	background: var(--color-error2);
	color: var(--color-text);
}

.reset-button:hover {
	transform: none !important;
	background: var(--color-error) !important;
}
</style>